import {HttpClient, HttpErrorResponse, HttpEvent, HttpResponse} from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';
import {firstValueFrom, throwError, Observable} from 'rxjs';

import {catchError, first, map} from 'rxjs/operators';

import {ResponsePaginateSuccess, ResponseSuccess} from '@atmi/core/models';
import {generateIndexQueryString, IndexQueryStringType} from '@atmi/core/utils';

import {MediaModuleConfiguration} from '../models/configuration';
import {Media} from '../models/media';
import {MediaConfigurationService} from '../token';

async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, _) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.readAsDataURL(blob);
  });
}

@Injectable({
  providedIn: 'root',
})
export class MediaService {

  private readonly path = `${this.config.basePath}/files`;

  constructor(
    @Inject(MediaConfigurationService) private readonly config: MediaModuleConfiguration,
    private readonly http: HttpClient,
  ) {
  }

  /**
   * @param consumes string[] mime-types
   * @return true: consumes contains 'multipart/form-data', false: otherwise
   */
  private canConsumeForm(consumes: string[]): boolean {
    const form = 'multipart/form-data';
    for (const consume of consumes) {
      if (form === consume) {
        return true;
      }
    }
    return false;
  }

  getImageBase64(imageUri: string) {
    return this.http.get<string>(`${this.config.basePath}/${imageUri}`).pipe(first());
  }

  generateKey() {
    return this.config.binaryKeyGenerator ? this.config.binaryKeyGenerator() : '';
  }

  generateBinaryUrl(id: string) {
    const key = this.generateKey();
    let url = `${this.path}/${encodeURIComponent(String(id))}/content`;

    if (key) {
      url += `?key=${key}`;
    }

    return url;
  }

  index(queryString?: IndexQueryStringType) {
    const params = generateIndexQueryString(queryString);

    return this.http.get<ResponsePaginateSuccess<Media[]>>(this.path, {params});
  }

  get(id: string) {
    return this.http.get<ResponseSuccess<Media>>(`${this.path}/${id}`).pipe(
      map((res) => res.data),
    );
  }

  async getMany(ids: string[]) {
    return await Promise.all(ids.map(async (id) => firstValueFrom(this.get(id).pipe(first()))));
  }


  getBinary(id: string) {
    const url = this.generateBinaryUrl(id);
    return this.http.get(url, {responseType: 'blob'});
  }

  async getBase64FromUrl(url: string): Promise<string> {
    const res = await firstValueFrom(this.http.get(url, {responseType: 'blob'}).pipe(first()));
    return blobToBase64(res);
  }

  async getBase64Image(id: string): Promise<string> {
    const res = await firstValueFrom(this.getBinary(id).pipe(first()));
    return blobToBase64(res);
  }


  post<T = unknown>(
    filesInput: ReadonlyArray<Blob> | FileList,
    observe?: 'body',
    reportProgress?: boolean,
  ): Observable<T>;

  post<T = unknown>(
    filesInput: ReadonlyArray<Blob> | FileList,
    observe: 'events',
    reportProgress?: boolean,
  ): Observable<HttpEvent<T>>;

  post<T = unknown>(
    filesInput: ReadonlyArray<Blob> | FileList,
    observe: 'response',
    reportProgress?: boolean,
  ): Observable<HttpResponse<T>>;

  post<T = unknown>(
    filesInput: ReadonlyArray<Blob> | FileList,
    observe: 'body' | 'events' | 'response' = 'body',
    reportProgress: boolean = false,
  ) {
    const files = Array.from(filesInput ?? []);

    if (!files.length) {
      throw new Error('At least one file is required when calling mediaPost.');
    }

    files.forEach((file, index) => {
      if (!(file instanceof Blob)) {
        throw new TypeError(`Item at index ${index} is not a Blob.`);
      }

      if (file.size === 0) {
        throw new Error(`File at index ${index} is empty.`);
      }
    });

    const consumes: string[] = [
      'multipart/form-data',
    ];
    const canConsumeForm = this.canConsumeForm(consumes);

    if (!canConsumeForm) {
      throw new Error('Cannot upload media because the API does not accept multipart/form-data.');
    }

    const formData = new FormData();
    files.forEach((file, index) => {
      const filename = (file as File).name ?? `file-${index + 1}`;
      formData.append('file[]', file, filename);
    });

    const handleRequest = <O>(request$: Observable<O>): Observable<O> => request$.pipe(
      first(),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 413) {
          return throwError(() => new Error('Uploaded files exceed the maximum size allowed by the server.'));
        }
        if (error.status === 0) {
          return throwError(() => new Error('Network error occurred while uploading files. Please try again.'));
        }
        return throwError(() => error);
      }),
    );

    if (observe === 'events') {
      return handleRequest(
        this.http.post<T>(
          this.path,
          formData,
          {
            observe: 'events',
            reportProgress,
          },
        ),
      );
    }

    if (observe === 'response') {
      return handleRequest(
        this.http.post<T>(
          this.path,
          formData,
          {
            observe: 'response',
            reportProgress,
          },
        ),
      );
    }

    return handleRequest(
      this.http.post<T>(
        this.path,
        formData,
        {
          observe: 'body',
          reportProgress,
        },
      ),
    );
  }

  put(media: Media) {
    return this.http.put<ResponseSuccess<Media>>(`${this.path}/${media.id}`, media).pipe(
      map((res) => res.data),
      first(),
    );
  }


  remove(id: string) {
    return this.http.delete(`${this.path}/${id}`).pipe(first());
  }

}
